import { ListaSelecionadaService } from '@/app/core/service/lista-selecionada.service'
import { PermissaoListaService } from '@/app/core/service/permissao-lista.service'
import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Output, inject } from '@angular/core'
import { NavigationEnd, Router, RouterModule } from '@angular/router'
import { NgbCollapse, NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap'
import { SimplebarAngularModule } from 'simplebar-angular'

import { basePath } from '@/app/common/constants'
import { MenuItem } from '@/app/core/models/menu.model'
import { AuthenticationService } from '@/app/core/service/auth.service'
import { findAllParent, findMenuItem } from '@/app/core/utils'

import { LogoBoxComponent } from './logo-box/logo-box.component'
import { MENU_ITEMS } from './menu-items'

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    SimplebarAngularModule,
    LogoBoxComponent,
    RouterModule,
    NgbCollapseModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  menuItems = MENU_ITEMS
  activeMenuItems: string[] = []

  @Output() overlayClicked = new EventEmitter<void>()
  @Output() mobileNavigationRequested = new EventEmitter<void>()

  private readonly router = inject(Router)
  private readonly auth = inject(AuthenticationService)
  private readonly permissaoLista = inject(PermissaoListaService)
  private readonly listaSelecionada = inject(ListaSelecionadaService)

  get manual(): { url: string; versao: string } {
    const { arquivo, versao } = this.auth.isUsuarioExterno()
      ? { arquivo: 'manual_consulta_ampla.pdf', versao: '0.2 de 20/08/2026' }
      : { arquivo: 'manual_sistema.pdf', versao: '0.2 de 06/07/2026' }
    return { url: `/assets/manual/${arquivo}`, versao }
  }

  get manualUsuario(): { url: string; versao: string; rotulo: string } {
    return this.auth.isUsuarioExterno()
      ? { url: '/assets/manual/manual_usuario_externo.pdf', versao: '1.0 de 18/09/2026', rotulo: 'Manual do Colaborador (novo)' }
      : { url: '/assets/manual/manual_usuario.pdf?v=20261024', versao: '1.3 de 24/10/2026', rotulo: 'Manual do Usuário (novo)' }
  }

  trimmedURL = this.getTrimmedUrl()

  constructor() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.trimmedURL = this.getTrimmedUrl()
        this.activateMenu()
      }
    })
  }

  ngOnInit(): void {
    this.initMenu()
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.activateMenu()
    })
  }

  initMenu(): void {
    this.menuItems = this.filterMenuByRole(MENU_ITEMS)
  }

  toggleMenuItem(menuItem: MenuItem, collapse: NgbCollapse): void {
    collapse.toggle()

    if (!menuItem.collapsed) {
      const openMenuItems = [
        menuItem.key!,
        ...findAllParent(this.menuItems, menuItem),
      ]

      this.updateCollapsedState(this.menuItems, openMenuItems)
    }
  }

  activateMenu(): void {
    const div = document.querySelector('.navbar-nav')
    if (!div) return

    const items = div.getElementsByClassName('nav-link')
    let matchingMenuItem: Element | null = null

    for (let i = 0; i < items.length; i++) {
      const item = items[i] as HTMLAnchorElement
      if (this.trimmedURL === item.pathname) {
        matchingMenuItem = item
        break
      }
    }

    if (!matchingMenuItem) return

    const key = matchingMenuItem.getAttribute('aria-controls')
    if (!key) return

    const activeMenu = findMenuItem(this.menuItems, key)
    if (!activeMenu) return

    const matchingKeys = [
      activeMenu.key!,
      ...findAllParent(this.menuItems, activeMenu),
    ]

    this.activeMenuItems = matchingKeys
    this.updateCollapsedState(this.menuItems, matchingKeys, true)
  }

  hasSubmenu(menu: MenuItem): boolean {
    return !!menu.subMenu?.length
  }

  requestMobileNavigationClose(): void {
    this.mobileNavigationRequested.emit()
  }

  private getTrimmedUrl(): string {
    return this.router.url.replaceAll(
      basePath !== '' ? `${basePath}/` : '',
      '/'
    )
  }

  private updateCollapsedState(
    menus: MenuItem[],
    keysToKeepOpen: string[],
    inverse = false
  ): void {
    const apply = (menu: MenuItem) => {
      menu.subMenu?.forEach(apply)
      menu.collapsed = inverse
        ? !keysToKeepOpen.includes(menu.key!)
        : keysToKeepOpen.includes(menu.key!)
          ? menu.collapsed
          : true
    }

    menus.forEach(apply)
  }

  /** Itens com funcionalidades só aparecem para quem as tem no papel da lista selecionada. */
  visivelPeloPapel(item: MenuItem): boolean {
    if (!item.funcionalidades?.length) return true
    const listaId = this.listaSelecionada.getIdListaSelecionada()
    return item.funcionalidades.some((f) => this.permissaoLista.papelTem(listaId, f))
  }

  private filterMenuByRole(menu: MenuItem[]): MenuItem[] {
    const tipoUsuario = this.auth.getTipoUsuario()

    return menu
      .filter(item => {
        if (!item.roles?.length) {
          return true
        }

        return !!tipoUsuario && item.roles.includes(tipoUsuario)
      })
      .map(item => ({
        ...item,
        subMenu: item.subMenu
          ? this.filterMenuByRole(item.subMenu)
          : undefined,
      }))
      .filter(item => !item.subMenu || item.subMenu.length > 0)
  }
}
